// Branching shell:
// - signed-in users get the sidebar layout (`.app-shell`) — Sidebar + main.
// - signed-out users (login / signup / public home) get a minimal centred
//   shell with a small header lockup + footer. Same vertical structure as
//   before so unauth pages don't shift around.

import { Link, Outlet, useLocation } from "react-router";
import { useSession } from "../lib/auth";
import { Sidebar } from "./Sidebar";
import { Logomark } from "./Logomark";

// Routes that always render in the unauth shell, even when the user has
// a valid session cookie. Hitting /login or /signup while signed in
// immediately redirects to /dashboard (see those route components), but
// for the brief moment before the redirect fires we don't want the
// sidebar showing on the sign-in form.
const UNAUTH_PATHS = new Set(["/login", "/signup", "/"]);

export function AppLayout() {
  const { data: session } = useSession();
  const signedIn = Boolean(session?.user?.id);
  const location = useLocation();
  const forceUnauthShell = UNAUTH_PATHS.has(location.pathname);

  if (signedIn && !forceUnauthShell) {
    return (
      <div className="app-shell">
        <span className="paper-grain" aria-hidden="true" />
        <Sidebar />
        <main>
          {/* keyed wrapper remounts on every route change so the CSS
              `page-fade` keyframes re-fire and the new page's top-level
              sections cascade in. Sidebar stays mounted, so it doesn't
              flicker. */}
          <div className="page-fade" key={location.pathname}>
            <Outlet />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell-signed-out">
      <header style={{ borderBottom: "1px solid rgb(var(--color-border))" }}>
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Link to="/" className="lockup">
            <Logomark size={22} />
            <span className="wm">Klemento</span>
          </Link>
          <nav style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 14 }}>
            <Link to="/login" style={{ color: "rgb(var(--color-ink-muted))", fontWeight: 500 }}>
              Sign in
            </Link>
            <Link to="/signup" className="btn btn-primary">
              Sign up
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <Outlet />
      </main>

      <footer
        style={{
          borderTop: "1px solid rgb(var(--color-border))",
          padding: "24px",
          fontSize: 13,
          color: "rgb(var(--color-ink-muted))",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <span>Klemento. Coaching for the meetings you already have.</span>
          <span>
            <a href="https://klemento.com/" style={{ marginRight: 16 }}>
              klemento.com
            </a>
            <a href="https://klemento.com/privacy/">Privacy</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
